/**
 * The browser's half of the identity: one Ed25519 key pair, kept in `localStorage`.
 *
 * There is no sign-up. A first visit mints a key and the server gives it a guest account;
 * the optional password exists only so the same account can be reached from a second
 * device. Losing the key means becoming a new guest, which is the honest trade for not
 * asking anyone for an email address to play a board game.
 */

import {
  createIdentity,
  exportIdentity,
  type Identity,
  importIdentity,
} from '@gok/shared';

const STORAGE_KEY = 'gok.identity';

/** Load the stored identity, minting and saving a new one if there is not a usable one. */
export async function loadIdentity(): Promise<Identity> {
  const stored = read(STORAGE_KEY);
  if (stored) {
    const identity = await importIdentity(stored);
    if (identity) return identity;
    // Corrupt or from an older format. Better a new guest than a broken session.
    console.warn('Stored identity could not be restored; starting a new one.');
  }

  const identity = await createIdentity();
  write(STORAGE_KEY, await exportIdentity(identity));
  return identity;
}

/** Private browsing and blocked storage both throw rather than returning null. */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A guest who cannot be remembered is still a guest who can play.
  }
}
